import React, {useEffect, useRef, useState} from "react";
/** @jsxImportSource @emotion/react */
import {css} from "@emotion/react";
import styled from "@emotion/styled";
import {useApplicationContext} from "../../context/ApplicationContextProvider";
import {HEADER_TYPE_BASE_MOBILE_LOGO_ONLY} from "../_common/section/Header";
import {FOOTER_TYPE_BASE} from "../_common/section/Footer";
import InfoCard from "./component/InfoCard";
import CourseList from "./component/CourseList";
import {useMobileDetect, useMountEffect} from "../../utils/Hooks";
import {useModelContext} from "../../context/ModelContextProvider";
import {ATTENDANCE_OBJ_INIT_SET, NAV_TAB_NUM_COURSE, NAV_TAB_NUM_INFO} from "../../constant/options";
import {useTheme} from "@emotion/react";
import Log from "../../common/Log";

export const CARD_TYPE_PERSONAL = 1; // 개인정보
export const CARD_TYPE_ATTENDANCE_RATE = 2; // 수강률
export const CARD_TYPE_ATTENDANCE_TIME = 3; // 수강시간
export const CARD_TYPE_MOBILE_PERSONAL = 4; // 모바일 개인정보

const ContainerCourseList = () => {
    // ApplicationContext
    useApplicationContext({
        header: {
            type: HEADER_TYPE_BASE_MOBILE_LOGO_ONLY,
        },
        footer: {
            type: FOOTER_TYPE_BASE,
        },
    });

    const {is_mobile} = useMobileDetect();
    const {isSuccess, isError, account_model} = useModelContext();

    // state
    const [course_list, setCourseList] = useState([]);
    const [attendance_obj, setAttendanceObj] = useState(ATTENDANCE_OBJ_INIT_SET);
    const [mobile_nav_tab_num, setMobileNavTabNum] = useState(NAV_TAB_NUM_COURSE);
    const [is_loading, setIsLoading] = useState(false);

    // ref
    const start_x = useRef();
    const end_x = useRef();

    // css style
    const {css, view} = useComponentStyle(is_mobile);

    useMountEffect(() => {
        updateUi();
    });

    const updateUi = async () => {
        setIsLoading(true);
        const course_list_res = await account_model.getCourseList();

        if (isSuccess(course_list_res)) {
            setCourseList(course_list_res.data.my_courses);
            setAttendanceObj({
                this_month: course_list_res.data.attendance_time.this_month,
                total: course_list_res.data.attendance_time.total,
                total_attendance_rate: course_list_res.data.total_attendance_rate,
            });
        } else if (isError(course_list_res)) {
            Log.private("getCourseList 에러 : ", course_list_res);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (is_mobile) {
            document.addEventListener("touchstart", touchStart);
            document.addEventListener("touchend", touchEnd);
        }

        return () => {
            document.removeEventListener("touchstart", touchStart);
            document.removeEventListener("touchend", touchEnd);
        };
    }, [is_mobile]);

    const touchStart = (e) => {
        start_x.current = e.touches[0].pageX;
    };

    const touchEnd = (e) => {
        end_x.current = e.changedTouches[0].pageX;
        let diff = end_x.current - start_x.current;

        // 터치 후 좌, 우 드래그 거리가 150 이하인 경우 nav_tab 변경 X
        if (diff >= 150) {
            setMobileNavTabNum(NAV_TAB_NUM_COURSE);
        } else if (diff <= -150) {
            setMobileNavTabNum(NAV_TAB_NUM_INFO);
        }
    };

    return (
        <div css={css.course_list_container_css}>
            {is_mobile && (
                <view.MobileNavTabWrapper>
                    <view.MobileNavTabItem
                        is_active={mobile_nav_tab_num === NAV_TAB_NUM_COURSE}
                        onClick={() => setMobileNavTabNum(NAV_TAB_NUM_COURSE)}>
                        <span>클래스</span>
                    </view.MobileNavTabItem>
                    <view.MobileNavTabItem
                        is_active={mobile_nav_tab_num === NAV_TAB_NUM_INFO}
                        onClick={() => setMobileNavTabNum(NAV_TAB_NUM_INFO)}>
                        <span>MY</span>
                    </view.MobileNavTabItem>
                </view.MobileNavTabWrapper>
            )}

            {(!is_mobile || mobile_nav_tab_num === NAV_TAB_NUM_INFO) && (
                <view.InfoSection>
                    <InfoCard
                        css={is_mobile ? {margin: "1.5em 0"} : {margin: ".8em 0"}}
                        type={is_mobile ? CARD_TYPE_MOBILE_PERSONAL : CARD_TYPE_PERSONAL}
                        is_loading={is_loading}
                    />
                    <InfoCard
                        css={is_mobile ? {marginBottom: "1.5em"} : {marginBottom: ".8em"}}
                        type={CARD_TYPE_ATTENDANCE_RATE}
                        attendance_obj={attendance_obj}
                        is_loading={is_loading}
                    />
                    <InfoCard
                        css={is_mobile ? {marginBottom: "1.5em"} : {marginBottom: ".8em"}}
                        type={CARD_TYPE_ATTENDANCE_TIME}
                        attendance_obj={attendance_obj}
                        is_loading={is_loading}
                    />
                </view.InfoSection>
            )}

            {(!is_mobile || mobile_nav_tab_num === NAV_TAB_NUM_COURSE) && (
                <CourseList course_list={course_list} is_loading={is_loading} />
            )}
        </div>
    );
};

export default ContainerCourseList;

export const useComponentStyle = (is_mobile) => {
    const theme = useTheme();
    return {
        css: {
            course_list_container_css: css({
                width: "100%",
                maxWidth: "1128px",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                flexDirection: is_mobile ? "column" : "row",
                alignItems: is_mobile && "center",
                paddingTop: is_mobile && "4em",
                margin: "0 auto",
            }),
        },
        view: {
            InfoSection: styled.div({
                width: is_mobile ? "95%" : "30%",
                minWidth: "260px",
                height: "100%",
                paddingTop: is_mobile && "4em",
                marginLeft: !is_mobile && "10px",
                "@media(max-width: 320px)": {
                    paddingTop: is_mobile && "3em",
                },
            }),
            MobileNavTabWrapper: styled.div({
                width: "100%",
                height: "4em",
                top: "4em",
                display: "flex",
                position: "fixed",
                zIndex: "6001",
                backgroundColor: theme.colors.w,
                transition: "all .2s",
                "@media (max-width: 320px)": {
                    height: "3em",
                },
            }),
            MobileNavTabItem: styled.div((props) => ({
                width: "50%",
                cursor: "pointer",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderBottom: `3px solid ${props.is_active ? theme.colors.bearu_primary : theme.colors.lg1}`,
                fontSize: theme.font_size.f20,
                fontWeight: props.is_active && "700",
                color: props.is_active && theme.colors.bearu_primary,
                transition: "all .2s",
                "@media (max-width: 320px)": {
                    fontSize: theme.font_size.f17,
                },
            })),
        },
    };
};
